/**
 * C2PA Timestamp Validator
 *
 * Validates C2PA signatures according to RFC 3161 timestamp requirements.
 * Per C2PA specification, signatures with expired certificates are still valid
 * if they contain trusted timestamps proving the signature was created during
 * the certificate's validity period.
 */

import type { ValidationItem } from '../types/metadata'

/**
 * Validation results structure from C2PA SDK
 */
interface ValidationResults {
  activeManifest?: {
    success?: ValidationItem[]
    failure?: ValidationItem[]
    informational?: ValidationItem[]
  }
}

/**
 * Signature information structure
 */
interface SignatureInfo {
  issuer?: string
  time?: string
  certSerialNumber?: string
  alg?: string
}

/**
 * Check if validation results contain a trusted or validated RFC 3161 timestamp
 *
 * A timestamp is considered valid if it appears in the success array as either:
 * - 'timeStamp.trusted': Timestamp from a trusted TSA in the local trust store
 * - 'timeStamp.validated': Timestamp cryptographically valid (proves signature timing)
 *
 * Per RFC 3161, a validated timestamp provides cryptographic proof that the signature
 * was created at a specific time, even if the TSA certificate isn't locally trusted.
 *
 * @param validationResults - C2PA validation results object
 * @returns true if a valid timestamp exists in success array
 */
export function hasValidTimestamp(validationResults: ValidationResults | null | undefined): boolean {
  if (!validationResults || !validationResults.activeManifest) {
    return false
  }

  const successCodes = validationResults.activeManifest.success || []
  // Accept either timeStamp.trusted or timeStamp.validated
  // timeStamp.trusted = timestamp is from a trusted TSA
  // timeStamp.validated = timestamp signature is valid (may not be in trust list)
  return successCodes.some(item =>
    item.code === 'timeStamp.trusted' || item.code === 'timeStamp.validated'
  )
}

/**
 * Check if an error code is related to certificate validity issues that
 * can be ignored when a valid timestamp exists
 *
 * Certificate-related errors that are ignorable with valid timestamp:
 * - signingCredential.expired: Certificate expired after signature
 * - signingCredential.untrusted: Certificate not in trust list
 * - signingCredential.revoked: Certificate revoked after signature
 *
 * Integrity errors are NEVER ignorable:
 * - assertion.hashMismatch: Content tampered
 * - claim.signature.invalid: Signature cryptographically invalid
 * - manifest.integrity.failed: Manifest corrupted
 *
 * @param code - Validation error code from C2PA SDK
 * @returns true if error can be ignored with valid timestamp
 */
export function isTimestampIgnorableError(code: string): boolean {
  const ignorableWithTimestamp = [
    'signingCredential.expired',
    'signingCredential.untrusted',
    'signingCredential.revoked'
  ]

  return ignorableWithTimestamp.includes(code)
}

/**
 * Check if ALL errors in the failure array can be ignored due to valid timestamp
 *
 * Returns true only if every single error is timestamp-ignorable.
 * If even one error is NOT timestamp-ignorable (e.g., tampering), returns false.
 *
 * @param failures - Array of validation failure items
 * @returns true if all errors are timestamp-ignorable, false otherwise
 */
export function allErrorsAreTimestampIgnorable(failures: ValidationItem[]): boolean {
  if (!failures || failures.length === 0) {
    return true
  }

  return failures.every(failure => isTimestampIgnorableError(failure.code))
}

/**
 * Check if the signer's certificate is explicitly trusted
 *
 * A signer is trusted if:
 * - 'signingCredential.trusted' appears in the success array
 * - AND 'signingCredential.untrusted' does NOT appear in the failure array
 *
 * Note: A file can have valid cryptographic integrity (not tampered) even
 * with an untrusted signer. Trusted signer = identity verified by a known CA.
 *
 * @param validationResults - C2PA validation results object
 * @returns true if signer is explicitly trusted in a known CA, false otherwise
 */
export function isSignerTrusted(validationResults: ValidationResults | null | undefined): boolean {
  if (!validationResults || !validationResults.activeManifest) {
    return false
  }

  const failures = validationResults.activeManifest.failure || []
  // If signingCredential.untrusted is in failures, signer is not trusted
  if (failures.some(f => f.code === 'signingCredential.untrusted')) {
    return false
  }

  const successes = validationResults.activeManifest.success || []
  // Check for explicit trust success code
  return successes.some(s => s.code === 'signingCredential.trusted')
}

/**
 * Check if a file is "Historically Valid" — content integrity is confirmed via
 * RFC 3161 timestamp, but the signing certificate has since expired.
 *
 * This is the most common real-world case for major platforms (Google, Adobe, etc.)
 * whose signing certificates expire after 1-3 years. The RFC 3161 timestamp provides
 * cryptographic proof that the signature was created while the certificate was still
 * valid — so the content's integrity is preserved indefinitely.
 *
 * Differs from plain "Valid" (untrusted CA):
 * - "Valid": cert is not expired but the CA is not in the local trust store
 * - "Historically Valid": cert expired (was valid at signing), timestamp proves timing
 *
 * @param validationResults - C2PA validation results object
 * @returns true if cert expired but RFC 3161 timestamp proves integrity at signing time
 */
export function isHistoricallyAuthentic(validationResults: ValidationResults | null | undefined): boolean {
  if (!validationResults?.activeManifest) return false

  const failures = validationResults.activeManifest.failure || []

  // Must have a cert-expiry failure — this is the distinguishing factor
  const hasCertExpired = failures.some(f => f.code === 'signingCredential.expired')
  if (!hasCertExpired) return false

  // Must have a valid timestamp AND all failures must be timestamp-ignorable (no tampering)
  return hasValidTimestamp(validationResults) && allErrorsAreTimestampIgnorable(failures)
}

/**
 * Calculate true validation status considering timestamp evidence
 *
 * Validation Logic:
 * 1. If no signature info exists → Invalid
 * 2. If no failures exist → Valid
 * 3. If failures exist:
 *    a. Check if valid timestamp exists
 *    b. Check if ALL failures are timestamp-ignorable (cert expiry, untrusted, revoked)
 *    c. If both true → Valid (timestamp proves content integrity)
 *    d. Otherwise → Invalid (integrity errors cannot be overridden)
 *
 * IMPORTANT: "Valid" here means cryptographic integrity is confirmed.
 * It does NOT necessarily mean the signer is trusted. Use isSignerTrusted()
 * separately to determine if the signer's identity is verified.
 *
 * @param validationResults - C2PA validation results object
 * @param signatureInfo - Signature information (null if unsigned)
 * @returns true if signature is cryptographically valid (considering timestamps), false otherwise
 */
export function calculateTrueValidationStatus(
  validationResults: ValidationResults | null | undefined,
  signatureInfo: SignatureInfo | null | undefined
): boolean {
  // No signature info means unsigned content
  if (!signatureInfo) {
    return false
  }

  // Handle null/undefined validation results
  if (!validationResults || !validationResults.activeManifest) {
    // If validation results are missing, we cannot determine validity
    // Per C2PA spec, validation results are required for proper verification
    return false
  }

  const failures = validationResults.activeManifest.failure || []

  // No failures = valid signature
  if (failures.length === 0) {
    return true
  }

  // Failures exist - check if they can be ignored due to valid timestamp
  const timestampIsValid = hasValidTimestamp(validationResults)
  const allFailuresAreIgnorable = allErrorsAreTimestampIgnorable(failures)

  // If we have a valid timestamp AND all failures are certificate-related,
  // the signature is valid (timestamp proves signature was created when cert was valid)
  if (timestampIsValid && allFailuresAreIgnorable) {
    return true
  }

  // Otherwise, failures indicate invalid signature
  // (either no timestamp, untrusted timestamp, or tampering/integrity errors)
  return false
}
